import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import Layout from '../Layout'

interface ImageFile {
  id: string
  url: string
  filename: string
  content_type: string
  byte_size: number
}

interface Account {
  id: number
  name: string
  slug: string
  price_input_mode?: number
}

interface Product {
  id: number
  name: string
  sku: string
  price: number
  price_display?: number
  cost_price: number
  stock_quantity: number
  description: string
  track_variants: boolean
  category?: { id: number; name: string }
  product_images?: ImageFile[]
  tax_rate: number
  unit_type: number | string
  track_inventory: boolean
  requires_serial: boolean
  max_web_inventory: number | null
}

interface Props {
  product: Product
  categories: Array<{ id: number; name: string }>
  account: Account
  errors?: string[]
  permissions?: { can_manage_products: boolean }
}

export default function ProductsEdit({ product, categories, account, errors }: Props) {
  const hasVariants = product.track_variants
  const withTax = account.price_input_mode === 1

  const [name, setName] = useState(product.name || '')
  const [sku, setSku] = useState(product.sku || '')
  const [price, setPrice] = useState(withTax && product.price_display != null ? product.price_display.toString() : product.price?.toString() || '')
  const [costPrice, setCostPrice] = useState(product.cost_price?.toString() || '')
  const [stock, setStock] = useState(product.stock_quantity?.toString() || '0')
  const [description, setDescription] = useState(product.description || '')
  const [categoryId, setCategoryId] = useState(product.category?.id?.toString() || '')

  const [taxRate, setTaxRate] = useState(product.tax_rate?.toString() || '')
  const [unitType, setUnitType] = useState((product.unit_type ?? 'unit').toString())
  const [trackInventory, setTrackInventory] = useState(product.track_inventory ?? true)
  const [requiresSerial, setRequiresSerial] = useState(product.requires_serial ?? false)
  const [maxWebInventory, setMaxWebInventory] = useState(product.max_web_inventory?.toString() || '')

  const [images, setImages] = useState<File[]>([])
  const [productImages, setProductImages] = useState<ImageFile[]>(product.product_images || [])
  const [deletingImage, setDeletingImage] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    const formData = new FormData()
    formData.append('product[name]', name)
    formData.append('product[sku]', sku)
    formData.append('product[price]', price)
    formData.append('product[cost_price]', costPrice)
    formData.append('product[stock_quantity]', hasVariants ? String(product.stock_quantity) : stock)
    formData.append('product[description]', description)
    if (categoryId) formData.append('product[category_id]', categoryId)
    formData.append('product[track_variants]', hasVariants ? 'true' : 'false')

    formData.append('product[tax_rate]', taxRate || '')
    formData.append('product[unit_type]', unitType)
    formData.append('product[track_inventory]', trackInventory ? 'true' : 'false')
    formData.append('product[requires_serial]', requiresSerial ? 'true' : 'false')
    if (maxWebInventory) formData.append('product[max_web_inventory]', maxWebInventory)

    images.forEach((img) => formData.append('product[images][]', img))

    router.put(`/${account.slug}/products/${product.id}`, formData, {
      onFinish: () => setSubmitting(false),
    })
  }

  const handleDeleteImage = (imageId: string) => {
    setDeletingImage(imageId)
    router.delete(`/${account.slug}/products/${product.id}/destroy_product_image`, {
      data: { image_signed_id: imageId },
      onSuccess: () => {
        setProductImages(productImages.filter(img => img.id !== imageId))
        setDeletingImage(null)
      },
      onError: () => setDeletingImage(null),
    })
  }

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  return (
    <Layout account={account}>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-[#202020]">Editar Producto</h1>
          <div className="flex gap-2">
            <Link href={`/${account.slug}/products`} className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded text-sm hover:bg-gray-300 transition-colors">← Volver</Link>
            {hasVariants && (
              <Link href={`/${account.slug}/products/${product.id}/variants`} className="bg-[#55ACEE] text-white px-3 py-1.5 rounded text-sm hover:bg-[#4a9dd4] transition-colors">Gestionar variantes →</Link>
            )}
          </div>
        </div>

        {errors && errors.length > 0 && (
          <div className="bg-red-50 text-red-600 p-3 rounded mb-4">{errors.map((error, i) => <p key={i}>{error}</p>)}</div>
        )}

        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <p className="text-sm font-medium text-gray-700 mb-1">Tipo:</p>
          {hasVariants ? (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded text-sm font-medium bg-[#55ACEE] text-white">Producto con variantes</span>
          ) : (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded text-sm font-medium bg-gray-200 text-gray-700">Producto simple</span>
          )}
        </div>

        {/* ⚙️ Configuración */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <p className="text-sm font-medium text-gray-700 mb-3">⚙️ Configuración</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ISV (%)</label>
              <input type="number" step="0.01" min="0" max="100" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" placeholder="0" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de unidad</label>
              <select value={unitType} onChange={(e) => setUnitType(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]">
                <option value="unit">Unidad</option>
                <option value="weight">Peso</option>
                <option value="meter">Metro</option>
              </select>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={trackInventory} onChange={(e) => setTrackInventory(e.target.checked)} className="accent-[#55ACEE] w-4 h-4" />
              <span className="text-sm text-gray-700">Rebaja inventario</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={requiresSerial} onChange={(e) => setRequiresSerial(e.target.checked)} className="accent-[#55ACEE] w-4 h-4" />
              <span className="text-sm text-gray-700">Requiere número de serie al vender</span>
            </label>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Tope web inventory</label>
            <input type="number" min="0" value={maxWebInventory} onChange={(e) => setMaxWebInventory(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" placeholder="Sin límite" />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" required />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
              <input type="text" value={sku} onChange={(e) => setSku(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]">
                <option value="">Sin categoría</option>
                {categories.map((cat) => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Precio {withTax ? '(con ISV)' : '(sin ISV)'} *</label>
              <input type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Costo</label>
              <input type="number" step="0.01" min="0" value={costPrice} onChange={(e) => setCostPrice(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" />
            </div>
          </div>

          {!hasVariants && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stock</label>
              <input type="number" min="0" value={stock} onChange={(e) => setStock(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Agregar imágenes</label>
            <input type="file" multiple accept="image/*" onChange={(e) => setImages(e.target.files ? Array.from(e.target.files) : [])} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" />
            {images.length > 0 && (<p className="text-sm text-gray-500 mt-1">{images.length} archivo(s) seleccionado(s)</p>)}
          </div>

          <div className="flex justify-end space-x-2">
            <Link href={`/${account.slug}/products`} className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50">Cancelar</Link>
            <button type="submit" disabled={submitting} className="bg-[#FFC107] text-[#202020] px-4 py-2 rounded font-semibold hover:bg-[#e6ad06] transition-colors disabled:opacity-50">{submitting ? 'Guardando...' : 'Actualizar Producto'}</button>
          </div>
        </form>

        {/* Product Images */}
        <div className="bg-white p-6 rounded-lg shadow mt-6">
          <h3 className="text-lg font-semibold text-[#202020] mb-4">Imágenes ({productImages.length})</h3>
          {productImages.length === 0 ? (
            <p className="text-sm text-gray-400">Sin imágenes.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {productImages.map((img) => (
                <div key={img.id} className="relative group border border-gray-200 rounded-lg overflow-hidden">
                  <img src={img.url} alt={img.filename} className="w-full h-28 object-cover" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                    <button onClick={() => handleDeleteImage(img.id)} disabled={deletingImage === img.id} className="opacity-0 group-hover:opacity-100 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-all disabled:opacity-50">
                      {deletingImage === img.id ? (<span className="text-xs">...</span>) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      )}
                    </button>
                  </div>
                  <div className="px-2 py-1 bg-gray-50 text-xs text-gray-500 truncate">{img.filename} · {formatBytes(img.byte_size)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}
